import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import { EditableCollectionBinding, useCollectionBinding } from '@vibesites/runtime/react';
import { RichTextContent } from './shared/RichTextContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { usePropertyWithBedroomOptions } from '../hooks/usePropertyWithBedroomOptions';

// ============================================================================
// Types
// ============================================================================

interface Faq {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

interface FaqGroup {
  category: string;
  items: Array<Faq & { originalIndex: number }>;
}

// ============================================================================
// Helpers
// ============================================================================

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

const FLOOR_PLANS_HREF = '/floor-plans';
const OCCUPANCY_QUALIFICATIONS_HREF =
  'https://www.pricebrotherskc.com/ResidentialRentalQualifications/';

/**
 * Apply a text transform only to HTML text nodes that are not already
 * inside an `<a>` tag — never nest links or rewrite tag attributes.
 */
function mapOutsideAnchors(html: string, transform: (text: string) => string): string {
  const parts = html.split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi);
  return parts
    .map((part, i) => {
      if (i % 2 === 1) return part;
      return part.replace(/(<[^>]+>)|([^<]+)/g, (_match, tag: string | undefined, text: string | undefined) =>
        tag ? tag : transform(text ?? ''),
      );
    })
    .join('');
}

/**
 * Company FAQ copy ships without inline links. Wire the two standard
 * cross-links here so every Price Brothers property gets them without
 * editing CMS FAQ richtext per site.
 */
function enrichFaqAnswerLinks(html: string): string {
  let result = mapOutsideAnchors(html, (text) =>
    text.replace(
      /Floor Plans/gi,
      (match) => `<a href="${FLOOR_PLANS_HREF}">${match}</a>`,
    ),
  );

  result = mapOutsideAnchors(result, (text) =>
    text.replace(
      /(occupancy qualifications\s+)(here)/gi,
      (_match, prefix: string, here: string) =>
        `${prefix}<a href="${OCCUPANCY_QUALIFICATIONS_HREF}" target="_blank" rel="noopener noreferrer">${here}</a>`,
    ),
  );

  return result;
}

/**
 * Strip HTML from a richtext value so we can decide whether an "answer"
 * is meaningful. A single empty `<p></p>` (which many rich-text editors
 * emit for a cleared field) collapses to '', so it's treated as empty.
 */
function isRichTextEmpty(
  html: string | null | undefined,
  property: Record<string, unknown>,
): boolean {
  if (!html) return true;
  return resolveRichText(html, property)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length === 0;
}

/**
 * Group FAQs by category, preserving the collection order both within and
 * across groups. Items without a category collapse into an "General"
 * bucket, but only when at least one item DOES have a category — otherwise
 * we render a single flat list with no category headings so a property
 * that hasn't categorized anything doesn't get a bogus "General" header.
 */
function groupByCategory(faqs: Faq[]): { groups: FaqGroup[]; showHeadings: boolean } {
  const anyCategorized = faqs.some((f) => hasValue(f.category));
  if (!anyCategorized) {
    return {
      groups: [
        {
          category: '',
          items: faqs.map((f, i) => ({ ...f, originalIndex: i })),
        },
      ],
      showHeadings: false,
    };
  }

  const order: string[] = [];
  const map = new Map<string, FaqGroup>();

  faqs.forEach((f, i) => {
    const cat = hasValue(f.category) ? f.category!.trim() : 'General';
    if (!map.has(cat)) {
      order.push(cat);
      map.set(cat, { category: cat, items: [] });
    }
    map.get(cat)!.items.push({ ...f, originalIndex: i });
  });

  return { groups: order.map((c) => map.get(c)!), showHeadings: true };
}

// ============================================================================
// Accordion item
// ============================================================================

interface FaqItemProps {
  faq: Faq;
  open: boolean;
  onToggle: () => void;
  itemId: string;
  property: Record<string, unknown>;
}

function FaqItem({ faq, open, onToggle, itemId, property }: FaqItemProps) {
  const question = hasValue(faq.question) ? faq.question! : 'Question';
  const answerHasContent = !isRichTextEmpty(faq.answer, property);
  const answerHtml = answerHasContent
    ? enrichFaqAnswerLinks(resolveRichText(faq.answer || '', property))
    : '';

  return (
    <div className={`faq-item${open ? ' faq-item--open' : ''}`}>
      <button
        type="button"
        className="faq-item-button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${itemId}-panel`}
        id={`${itemId}-button`}
      >
        <span className="faq-item-question">{question}</span>
        <span className="faq-item-icon" aria-hidden="true">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      <div
        id={`${itemId}-panel`}
        role="region"
        aria-labelledby={`${itemId}-button`}
        className="faq-item-panel"
        hidden={!open}
      >
        {answerHasContent ? (
          <div
            className="faq-item-answer prose"
            dangerouslySetInnerHTML={{ __html: answerHtml }}
          />
        ) : (
          <p className="faq-item-answer faq-item-answer--empty">
            No answer provided yet.
          </p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function FaqList() {
  const propertyRecord = usePropertyWithBedroomOptions();

  const rawFaqs = useCollectionBinding<Faq>('faqlist.items', {
    collection: 'faqs',
  });

  const faqs = rawFaqs || [];

  const { groups, showHeadings } = useMemo(() => groupByCategory(faqs), [faqs]);

  // Track which items are open. Keyed by the item's original index in the
  // collection so groupings don't invalidate the state when a category
  // reshuffles the display order.
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (idx: number) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQs">
          {faqs.length === 0 ? (
            <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
              <p className="font-body text-lg">
                Frequently asked questions will be displayed here.
              </p>
            </div>
          ) : (
            <div className="faq-groups">
              {groups.map((group) => (
                <div key={group.category || 'flat'} className="faq-group">
                  {showHeadings && group.category ? (
                    <h2 className="faq-group-heading">{group.category}</h2>
                  ) : null}
                  <div className="faq-list">
                    {group.items.map((item) => {
                      const idx = item.originalIndex;
                      const itemId = `faq-item-${idx}`;
                      return (
                        <FaqItem
                          key={itemId}
                          itemId={itemId}
                          faq={item}
                          open={openIndex === idx}
                          onToggle={() => toggle(idx)}
                          property={propertyRecord}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </EditableCollectionBinding>

        {/* Schema-lint anchor for the (unused-here) intro token so the
            static extractor can prove every schema key has a wrapper. It's
            hidden and never receives clicks — the token is authored via
            the editor sidebar. */}
        <RichTextContent
          path="faqlist.intro"
          defaultValue=""
          as="span"
          className="hidden"
          style={{ display: 'none' }}
        />
      </div>

      <style>{`
        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }
        .faq-group {
          display: flex;
          flex-direction: column;
        }
        .faq-group-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-primary);
          margin: 0 0 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .faq-item {
          border: 1px solid var(--vibe-color-border);
          background: #fff;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .faq-item:hover {
          border-color: color-mix(in srgb, var(--vibe-color-primary) 45%, var(--vibe-color-border));
        }
        .faq-item--open {
          border-color: var(--vibe-color-primary);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .faq-item-button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1rem 1.25rem;
          background: transparent;
          border: none;
          cursor: pointer;
          text-align: left;
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          line-height: 1.35;
          transition: color 0.2s ease;
        }
        .faq-item-button:hover {
          color: var(--vibe-color-primary);
        }
        .faq-item-button:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }

        .faq-item-question {
          flex: 1;
          min-width: 0;
        }

        .faq-item-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          color: var(--vibe-color-primary);
          transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .faq-item--open .faq-item-icon {
          transform: rotate(180deg);
        }

        .faq-item-panel {
          padding: 0 1.25rem 1.25rem;
        }

        .faq-item-answer {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--vibe-color-text);
        }
        .faq-item-answer :where(p, ul, ol) {
          margin: 0 0 0.6rem;
        }
        .faq-item-answer :where(p, ul, ol):last-child {
          margin-bottom: 0;
        }
        .faq-item-answer :where(ul, ol) {
          padding-left: 1.25rem;
        }
        .faq-item-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-item-answer--empty {
          color: var(--vibe-color-text-muted);
          font-style: italic;
        }

        @media (max-width: 600px) {
          .faq-item-button {
            padding: 0.85rem 1rem;
            font-size: 0.95rem;
          }
          .faq-item-panel {
            padding: 0 1rem 1rem;
          }
          .faq-item-answer {
            font-size: 0.9rem;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '',
    },
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FaqList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default FaqList;
